import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core'
import { ModeloDocumentoDados } from '../modelo-documento.model'
import { ModeloDocumentoService } from '../modelo-documento.service'
import { take } from 'rxjs'

@Component({
  selector: 'app-form-resultado-recurso',
  templateUrl: './form-resultado-recurso.component.html',
})
export class FormResultadoRecursoComponent implements OnInit {
  @Input() concursoId!: number
  @Input() tipoRecurso!: string
  @Output() formData = new EventEmitter<Record<string, string>>()

  dados?: ModeloDocumentoDados
  campos: Record<string, string> = {}

  // Caches pré-computados
  private candidatosCache: Record<string, any[]> = {}
  private notaMap: Record<string, string> = {}
  provas: string[] = []
  examinadores: string[] = []
  examMarkers: Record<string, string> = {}
  legendaSubstituicoes: { marker: string; nome: string; substituido: string }[] = []
  readonly provasFase1 = [1, 2, 3, 4]
  readonly provasFase2 = [1, 2, 3, 4, 5, 6, 7, 8]

  private readonly titulos: Record<string, string> = {
    RESULTADO_RECURSO_INSCRICOES: 'EDITAL DO RESULTADO DA ANÁLISE DO(S) RECURSO(S) CONTRA O INDEFERIMENTO DAS INSCRIÇÕES, SOLICITAÇÕES DE CONDIÇÕES ESPECIAIS, SOLICITAÇÕES DE LACTANTE E INSCRIÇÕES COMO PESSOA COM DEFICIÊNCIA',
    RESULTADO_RECURSO_COMISSAO: 'EDITAL DE RESULTADO DA ANÁLISE CONTRA A COMPOSIÇÃO DA COMISSÃO JULGADORA',
    RESULTADO_RECURSO_FASE1: 'EDITAL DE RESULTADO DA(S) ANÁLISE(S) DO(S) RECURSO(S) CONTRA O RESULTADO DAS PROVAS DA FASE I',
    RESULTADO_RECURSO_FASE2: 'EDITAL DO RESULTADO DA ANÁLISE DO(S) RECURSO(S) CONTRA OS RESULTADOS DAS PROVAS DA FASE II',
    DEFERIMENTO_INSCRICOES: 'EDITAL DE RESULTADO DA ANÁLISE DAS INSCRIÇÕES, SOLICITAÇÃO DE CONDIÇÕES ESPECIAIS, LACTANTES E INSCRIÇÕES COMO PESSOA COM DEFICIÊNCIA',
    TERMO_ENCERRAMENTO: 'TERMO DE ENCERRAMENTO DE INSCRIÇÕES EM CONCURSO PÚBLICO DE PROVAS E TÍTULOS PARA PROVIMENTO DE CARGO DE PROFESSOR DOUTOR',
    RETIFICACAO_EDITAL: 'EDITAL DE RETIFICAÇÃO DE ABERTURA / CALENDÁRIO',
    CANCELAMENTO_EDITAL: 'EDITAL DE CANCELAMENTO DA ABERTURA / DO CALENDÁRIO DE PROVAS',
    PRORROGACAO_INSCRICAO: 'EDITAL DE PRORROGAÇÃO DO PRAZO DE INSCRIÇÃO',
    REABERTURA_INSCRICOES: 'EDITAL DE REABERTURA DAS INSCRIÇÕES',
    COMISSAO_CALENDARIO: 'EDITAL DE CALENDÁRIO DE PROVAS',
    ALTERACAO_COMISSAO: 'EDITAL DE ALTERAÇÃO DA COMPOSIÇÃO DA COMISSÃO JULGADORA',
    ALTERACAO_CALENDARIO_FASE2: 'EDITAL DE ALTERAÇÃO DO CALENDÁRIO DE PROVAS DA FASE II',
    RESULTADO_FINAL: 'EDITAL DO RESULTADO FINAL',
    ATA_PROVA_ESCRITA: 'ATA DA PROVA ESCRITA',
    ATA_PLANO_TRABALHO: 'ATA DA PROVA DE ANÁLISE DO PLANO DE TRABALHO',
    ATA_PROVA_TITULOS: 'ATA DA PROVA DE TÍTULOS – FASE II',
    ATA_PROVA_DIDATICA: 'ATA DA PROVA DIDÁTICA – FASE II',
    ATA_PROVA_ARGUICAO: 'ATA DA PROVA DE ARGUIÇÃO – FASE II',
    ATA_PROVA_ESPECIFICA: 'ATA DA PROVA ESPECÍFICA',
  }

  get tituloDocumentoFormatado(): string {
    const titulo = this.titulos[this.tipoRecurso] || ''
    const tipo = this.campos['tipoEdital']
    if (!tipo) return titulo
    if (this.tipoRecurso === 'RETIFICACAO_EDITAL') {
      // "Abertura" ou "Calendário" (sem "de Provas")
      const label = tipo.replace(' de Provas', '').toUpperCase()
      return `EDITAL DE RETIFICAÇÃO DE ${label}`
    }
    if (this.tipoRecurso === 'CANCELAMENTO_EDITAL') {
      // "DA ABERTURA" ou "DO CALENDÁRIO DE PROVAS"
      if (tipo === 'Abertura') return 'EDITAL DE CANCELAMENTO DA ABERTURA'
      return 'EDITAL DE CANCELAMENTO DO CALENDÁRIO DE PROVAS'
    }
    return titulo
  }

  constructor(private service: ModeloDocumentoService) {}

  ngOnInit(): void {
    this.service.obterDados(this.concursoId, this.tipoRecurso)
      .pipe(take(1))
      .subscribe({
        next: d => {
          this.dados = d
          this.campos = { ...d.camposEditaveis }
          this.preComputar()
          this.emitir()
        },
        error: () => this.dados = undefined
      })
  }

  private preComputar(): void {
    if (!this.dados) return

    // Cache de candidatos por categoria
    this.candidatosCache = {}
    for (const c of this.dados.candidatos) {
      const cat = c.categoria || ''
      if (!this.candidatosCache[cat]) this.candidatosCache[cat] = []
      this.candidatosCache[cat].push(c)
    }

    // Cache de notas indexado por chave composta (chaveado por username do examinador efetivo)
    this.notaMap = {}
    this.examMarkers = {}
    this.legendaSubstituicoes = []
    if (this.dados.notasPorProva) {
      this.provas = Object.keys(this.dados.notasPorProva)
      const exSet = new Set<string>()
      for (const prova of this.provas) {
        for (const n of this.dados.notasPorProva[prova]) {
          const uname = n.examinadorUsername || ''
          if (!uname) continue
          exSet.add(uname)
          this.notaMap[`${prova}|${n.inscricaoId}|${uname}`] = n.nota
        }
      }
      // Ordenar por nome (paridade com o PDF / resultado-fase-1/2.ftl)
      this.examinadores = [...exSet].sort((a, b) => this.nomeExam(a).localeCompare(this.nomeExam(b)))

      // Marcadores de substituição (*, **, ...) na ordem (já ordenada por nome) + legenda
      const subs = this.dados.substituicoes || {}
      let mc = 0
      for (const uname of this.examinadores) {
        if (subs[uname]) {
          mc++
          const marker = '*'.repeat(mc)
          this.examMarkers[uname] = marker
          this.legendaSubstituicoes.push({ marker, nome: this.nomeExam(uname), substituido: subs[uname] })
        }
      }
    }
  }

  nomeExam(username: string): string {
    return this.dados?.examMap?.[username] || username
  }

  emitir(): void {
    // Validar se recursos que exigem resultado estão preenchidos
    let recursosValidos = true
    if (this.dados?.recursos && (this.tipoRecurso === 'RESULTADO_RECURSO_FASE1' || this.tipoRecurso === 'RESULTADO_RECURSO_FASE2' || this.tipoRecurso === 'RESULTADO_RECURSO_INSCRICOES')) {
      recursosValidos = this.dados.recursos.every(r => r.justificativa && r.justificativa.trim().length > 0)
    }
    this.campos['_recursosValidos'] = recursosValidos ? 'true' : 'false'
    if (this.dados?.recursos) {
      this.dados.recursos.forEach((r, i) => {
        this.campos['recurso_' + i + '_resultado'] = r.justificativa || ''
      })
    }
    this.formData.emit(this.campos)
  }

  onCampoChange(key: string, value: any): void {
    this.campos[key] = value ?? ''
    this.emitir()
  }

  onCamposChange(campos: Record<string, string>): void {
    this.campos = { ...this.campos, ...campos }
    this.emitir()
  }

  hasCampo(key: string): boolean {
    return Object.prototype.hasOwnProperty.call(this.campos, key)
  }

  candidatosPorCategoria(cat: string) {
    return this.candidatosCache[cat] || []
  }

  getNota(prova: string, inscricaoId: number, examinador: string): string {
    return this.notaMap[`${prova}|${inscricaoId}|${examinador}`] || ''
  }
}
